/**
 * Chart.js helpers for Drive / WebDAV statistics pages.
 * Extracted from the former Stimulus webdav-stats-charts controller so Preact
 * side pages and the Stimulus wrapper share one implementation.
 */

import {
  Chart,
  LineController,
  BarController,
  DoughnutController,
  LineElement,
  BarElement,
  ArcElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Legend,
  Tooltip,
  Filler,
} from 'chart.js'

Chart.register(
  LineController,
  BarController,
  DoughnutController,
  LineElement,
  BarElement,
  ArcElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Legend,
  Tooltip,
  Filler
)

const PALETTE = [
  'rgba(59, 130, 246, 0.85)',
  'rgba(16, 185, 129, 0.85)',
  'rgba(245, 158, 11, 0.85)',
  'rgba(239, 68, 68, 0.85)',
  'rgba(139, 92, 246, 0.85)',
  'rgba(236, 72, 153, 0.85)',
  'rgba(20, 184, 166, 0.85)',
  'rgba(107, 114, 128, 0.85)',
  'rgba(6, 182, 212, 0.85)',
]

/**
 * @param {object} opts
 * @param {HTMLCanvasElement|null} opts.trafficCanvas
 * @param {HTMLCanvasElement|null} opts.extensionsCanvas
 * @param {HTMLCanvasElement|null} opts.sizesCanvas
 * @param {string[]} opts.trafficLabels
 * @param {number[]} opts.trafficUploads
 * @param {number[]} opts.trafficDownloads
 * @param {string} opts.uploadsLabel
 * @param {string} opts.downloadsLabel
 * @param {string[]} opts.extensionLabels
 * @param {number[]} opts.extensionCounts
 * @param {string[]} opts.sizeLabels
 * @param {number[]} opts.sizeCounts
 * @param {string} opts.fileCountLabel
 * @returns {Chart[]}
 */
export function createStatsCharts(opts = {}) {
  const charts = []

  if (opts.trafficCanvas && opts.trafficLabels?.length) {
    charts.push(renderTraffic(opts.trafficCanvas, opts))
  }
  if (opts.extensionsCanvas && opts.extensionLabels?.length) {
    charts.push(renderExtensions(opts.extensionsCanvas, opts))
  }
  if (opts.sizesCanvas && opts.sizeLabels?.length) {
    charts.push(renderSizes(opts.sizesCanvas, opts))
  }

  return charts
}

export function destroyStatsCharts(charts) {
  ;(charts || []).forEach((chart) => {
    try {
      chart.destroy()
    } catch {
      // ignore
    }
  })
}

function renderTraffic(canvas, opts) {
  const labels = opts.trafficLabels
  const step = Math.max(1, Math.ceil(labels.length / 30))
  const displayLabels = labels.map((label, i) => (i % step === 0 ? label : ''))

  return new Chart(canvas, {
    type: 'line',
    data: {
      labels: displayLabels,
      datasets: [
        {
          label: opts.uploadsLabel,
          data: opts.trafficUploads,
          borderColor: PALETTE[0],
          backgroundColor: 'rgba(59, 130, 246, 0.10)',
          borderWidth: 2,
          pointRadius: labels.length <= 31 ? 3 : 0,
          tension: 0.3,
          fill: true,
        },
        {
          label: opts.downloadsLabel,
          data: opts.trafficDownloads,
          borderColor: PALETTE[1],
          backgroundColor: 'rgba(16, 185, 129, 0.10)',
          borderWidth: 2,
          pointRadius: labels.length <= 31 ? 3 : 0,
          tension: 0.3,
          fill: true,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { intersect: false, mode: 'index' },
      plugins: {
        legend: { position: 'bottom' },
        tooltip: { callbacks: { title: (items) => labels[items[0].dataIndex] || '' } },
      },
      scales: {
        x: { ticks: { maxRotation: 0, autoSkip: true } },
        y: { beginAtZero: true, ticks: { precision: 0 } },
      },
    },
  })
}

function renderExtensions(canvas, opts) {
  const labels = opts.extensionLabels
  const narrow = typeof window !== 'undefined' && window.matchMedia('(max-width: 767.98px)').matches

  return new Chart(canvas, {
    type: 'doughnut',
    data: {
      labels,
      datasets: [
        {
          data: opts.extensionCounts,
          backgroundColor: labels.map((_label, i) => PALETTE[i % PALETTE.length]),
          borderWidth: 0,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: narrow ? 'bottom' : 'right',
          labels: { boxWidth: 12, padding: narrow ? 8 : 12 },
        },
      },
    },
  })
}

function renderSizes(canvas, opts) {
  return new Chart(canvas, {
    type: 'bar',
    data: {
      labels: opts.sizeLabels,
      datasets: [
        {
          label: opts.fileCountLabel,
          data: opts.sizeCounts,
          backgroundColor: PALETTE[2],
          borderRadius: 4,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
    },
  })
}
